import React, { useState, useEffect } from 'react'; 
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext'; // ★ 언어 훅
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText, Search, X, Plus, Calendar, RotateCcw, AlertCircle, CheckCircle, Receipt, Printer,
  ChevronLeft, ChevronRight // ★ 페이지네이션 아이콘 추가
} from 'lucide-react';

// ==============================================================================================
//  [Sub Component] Settlement Detail Modal (정산서 출력용)
// ==============================================================================================
const SettlementDetailModal = ({ game, agent, onClose }) => {
  const { t } = useLang(); // ★ 번역 함수 사용
  if (!game) return null;

  const fmt = (num) => num ? Math.floor(Number(num)).toLocaleString() : '0';

  const calculateSheet = (g) => {
    const totalChipsBuy = parseFloat(g.sumBuyIn || 0);
    const totalCashOut = parseFloat(g.sumCashOut || 0);
    const gameWinLoss = totalChipsBuy - totalCashOut;
    
    const rollingAmount = parseFloat(g.totalRolling || 0);
    const commRate = parseFloat(g.rollingComm || 0);
    const rollingComm = Math.floor(rollingAmount * (commRate / 100));
    
    let netWinLoss = 0;
    let shareProfit = 0;

    if (g.type === 'Share') {
      netWinLoss = gameWinLoss - rollingComm;
      const shareRate = parseFloat(g.shareRate || 0);
      shareProfit = Math.floor(netWinLoss * (shareRate / 100));
    }
    return { totalChipsBuy, totalCashOut, gameWinLoss, rollingAmount, rollingComm, netWinLoss, shareProfit };
  };

  const calc = calculateSheet(game);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div id="print-overlay" style={{ 
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 
    }}>
      <div id="print-sheet" style={{ 
        backgroundColor: 'white', 
        width: '800px',        
        maxHeight: '90vh',     
        padding: '40px',       
        borderRadius: '8px', 
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)', 
        position:'relative',
        overflowY: 'auto'      
      }}>
        {/* --- Header Section --- */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'30px', borderBottom:'2px solid #0f172a', paddingBottom:'20px' }}>
          <div style={{ width:'120px', height:'60px', backgroundColor:'#e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'bold', color:'#64748b' }}>
            {t('lbl_comp_logo') || 'LOGO'} 
          </div>
          <div style={{ textAlign:'right' }}>
            {/* 정산서 제목 */}
            <h1 style={{ margin:0, fontSize:'28px', color:'#0f172a', letterSpacing:'1px', textTransform:'uppercase' }}>{t('menu_reports')}</h1>
            <p style={{ margin:'5px 0', fontSize:'16px', fontWeight:'bold', color:'#2563eb' }}>
              GAME: {game.id} &nbsp;|&nbsp; AGENT: {game.agentId}
            </p>
            <p style={{ margin:0, color:'#64748b', fontSize:'14px' }}>{t('date')}: {new Date().toLocaleDateString()}</p>
            <p style={{ margin:0, color:'#64748b', fontSize:'14px' }}>{t('staff')}: {game.settledBy || '-'}</p>
          </div>
        </div>

        {/* --- Body Section --- */}
        <div style={{ marginBottom:'40px', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'20px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', fontSize:'15px' }}>
              <span>{t('totalBuyIn')}</span>
              <span style={{ fontWeight:'bold' }}>{fmt(calc.totalChipsBuy)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', fontSize:'15px' }}>
              <span>{t('totalCashOut')}</span>
              <span style={{ fontWeight:'bold' }}>{fmt(calc.totalCashOut)}</span>
            </div>
            
            <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 15px', borderTop:'1px solid #cbd5e1', borderBottom:'1px solid #cbd5e1', marginBottom:'20px', background:'#f8fafc', borderRadius:'4px' }}>
              <span style={{ fontWeight:'bold', color:'#334155' }}>{t('gameWinLoss')}</span>
              <span style={{ fontWeight:'bold', fontSize:'16px', color: calc.gameWinLoss < 0 ? '#dc2626' : '#0f172a' }}>
                {fmt(calc.gameWinLoss)}
              </span>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', fontSize:'15px' }}>
              <span>{t('totalRolling')}</span>
              <span>{fmt(calc.rollingAmount)}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', fontSize:'15px' }}>
              <span>Commission %</span>
              <span>{game.rollingComm}%</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'20px', fontWeight:'bold', color:'#059669', fontSize:'15px' }}>
              <span>{t('rollingComm')}</span>
              <span>{fmt(calc.rollingComm)}</span>
            </div>

            {game.type === 'Share' && (
              <>
                <div style={{ borderTop:'2px dashed #cbd5e1', margin:'20px 0' }}></div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', fontSize:'15px' }}>
                  <span>{t('netWinLoss')} <span style={{fontSize:'11px', color:'#94a3b8'}}>(Win/Loss - Comm)</span></span>
                  <span style={{ fontWeight:'bold' }}>{fmt(calc.netWinLoss)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px', fontSize:'15px' }}>
                  <span>Share %</span>
                  <span>{game.shareRate}%</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'15px', background:'#ecfdf5', borderRadius:'6px', border:'1px solid #10b981', marginTop:'10px' }}>
                  <span style={{ fontWeight:'bold', color:'#047857' }}>{t('shareProfit')}</span>
                  <span style={{ fontWeight:'bold', fontSize:'18px', color:'#047857' }}>{fmt(calc.shareProfit)}</span>
                </div>
              </>
            )}
        </div>

        {/* --- Footer Signature Section --- */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:'60px', paddingBottom: '20px' }}>
          <div style={{ textAlign:'center', width:'40%' }}>
            <div style={{ borderBottom:'1px solid #334155', height:'40px', marginBottom:'10px' }}></div>
            <span style={{ fontWeight:'bold', fontSize:'14px' }}>{t('menu_cage')}</span>
          </div>
          <div style={{ textAlign:'center', width:'40%' }}>
            <div style={{ borderBottom:'1px solid #334155', height:'40px', marginBottom:'10px', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
                {agent ? `${agent.engSurname} ${agent.engGivenName}` : ''}
            </div>
            <span style={{ fontWeight:'bold', fontSize:'14px' }}>{t('menu_agents')}</span>
          </div>
        </div>

        <div className="no-print" style={{ position:'absolute', top:'20px', right:'20px', display:'flex', gap:'10px' }}>
          <button onClick={handlePrint} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'10px 15px', background:'#dc2626', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>
            <Printer size={16}/> {t('pdf')}
          </button>
          <button onClick={onClose} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'10px 15px', background:'#64748b', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>
            <X size={16}/> {t('close')}
          </button>
        </div>
      </div>
      
      <style>{`
        @media print {
            @page { size: auto; margin: 0; }
            html, body { height: 1px !important; overflow: hidden !important; background: white !important; margin: 0 !important; padding: 0 !important; }
            body * { visibility: hidden; }
            #print-overlay, #print-overlay * { visibility: visible; }
            #print-overlay { position: absolute !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; margin: 0 !important; padding: 20px !important; background: white !important; z-index: 99999 !important; }
            .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

// ==============================================================================================
//  [Main Component] Game List
// ==============================================================================================
const GameList = () => {
  // 1. Hooks
  const contextData = useData();
  const langContext = useLang(); // ★ 언어 훅
  const location = useLocation();
  const navigate = useNavigate();

  // 2. State Declarations
  const [filterType, setFilterType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // ★ [추가] 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false);
  
  const [selectedGameId, setSelectedGameId] = useState(null); 
  
  const [activeTab, setActiveTab] = useState('Rolling'); 
  const [amount, setAmount] = useState('');           
  const [cashChips, setCashChips] = useState('');     
  const [rollingChips, setRollingChips] = useState(''); 
  const [remark, setRemark] = useState('');

  const [txConfirm, setTxConfirm] = useState({ isOpen: false, data: null });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  const [newGameData, setNewGameData] = useState({
      agentId: '',
      currency: 'PHP', 
      type: 'Rolling',
      buyIn: '',
      rollingComm: '', 
      shareRate: ''
  });

  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);
  const [successModal, setSuccessModal] = useState({ isOpen: false, gameId: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 3. Data Loading
  const { 
      games = [], 
      agents = [], 
      addTransaction, 
      transactions = {}, 
      createGame,
      addSettlement,      
      updateGame,         
      processAgentTransaction,
      settings,
      currentUser
  } = contextData || {};
  
  const { t } = langContext || {}; // ★ t 함수 가져오기

  // ★ [수정] 권한 체크: 'Senior Manager'를 명시적으로 포함 (문자열 포함 방식 X)
  const isWritable = currentUser && ['Master_Admin', 'Senior Manager', 'Manager', 'Staff'].includes(currentUser.type);

  const fmt = (num) => num ? Math.floor(Number(num)).toLocaleString() : '0';
  const parseNum = (str) => parseFloat(String(str).replace(/,/g, '')) || 0;
 
  const safeGames = Array.isArray(games) ? games : [];
  const safeAgents = Array.isArray(agents) ? agents : [];

  const selectedGame = selectedGameId ? safeGames.find(g => g.id === selectedGameId) : null;

  const handleAgentClick = (e, agentCode) => {
      e.stopPropagation(); 
      navigate('/agents', { state: { targetAgentCode: agentCode } });
  };

  useEffect(() => {
    if (settings?.mainCurrency) {
        setNewGameData(prev => ({ ...prev, currency: settings.mainCurrency }));
    }
  }, [settings]);

  useEffect(() => {
      if (location.state?.targetGameId) {
          const targetId = location.state.targetGameId;
          const targetGame = safeGames.find(g => g.id === targetId);
          
          if (targetGame) {
              setSelectedGameId(targetId);
              setActiveTab('Rolling');
              setIsModalOpen(true);
              window.history.replaceState({}, document.title);
          }
      }
  }, [location.state, safeGames]);

  // ★ [추가] 필터 변경 시 페이지 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm, startDate, endDate]);

  if (!contextData || !langContext) {
      return (
          <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
              <h3>Loading data...</h3>
          </div>
      );
  }

  // --- Logic Functions ---

  const generateNextGameId = () => {
    if (!safeGames || safeGames.length === 0) return 'A00001';
    const ids = safeGames
      .map(g => g.id)
      .filter(id => id && id.startsWith('A'))
      .map(id => parseInt(id.substring(1), 10))
      .filter(num => !isNaN(num));

    if (ids.length === 0) return 'A00001';
    const maxId = Math.max(...ids);
    return 'A' + String(maxId + 1).padStart(5, '0');
  };

  const filteredGames = safeGames.filter(g => {
    if (filterType !== 'All' && g.type !== filterType) return false;
    const agent = safeAgents.find(a => a.code === g.agentId);
    const searchTarget = (
        (g.id || '') + 
        (agent ? agent.code : '') + 
        (agent ? (agent.engSurname || '') + (agent.engGivenName || '') : '') + 
        (agent ? agent.nativeName : '')
    ).toUpperCase();
    
    const query = searchTerm.toUpperCase();
    if (!searchTarget.includes(query)) return false;

    if (g.startedAt) {
        const gameDateStr = g.startedAt;
        if (startDate && gameDateStr < startDate) return false;
        if (endDate && gameDateStr > endDate) return false;
    }
    return true;
  });

  // ★ [추가] 페이지네이션 데이터 슬라이싱
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGames = filteredGames.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredGames.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
    }
  };

  const handleAmountChange = (setter) => (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setter(val ? Number(val).toLocaleString() : '');
  };

  const handleTabChange = (tab) => {
      setActiveTab(tab);
      setAmount('');
      setCashChips('');
      setRollingChips('');
      setRemark('');
  };

  const handleAgentInputChange = (e) => {
      const val = e.target.value.toUpperCase();
      setNewGameData({ ...newGameData, agentId: val });
      setShowAgentSuggestions(true);
  };

  const selectAgent = (agentCode) => {
      const agent = safeAgents.find(a => a.code === agentCode);
      let updates = { agentId: agentCode };
      if (agent && agent.rollingRate) {
          updates.rollingComm = agent.rollingRate;
      }
      setNewGameData(prev => ({ ...prev, ...updates }));
      setShowAgentSuggestions(false);
  };

  const agentSuggestions = safeAgents.filter(agent => {
      const input = newGameData.agentId.toUpperCase();
      const code = (agent.code || '').toUpperCase();
      const name = ((agent.engSurname || '') + (agent.engGivenName || '')).toUpperCase();
      const native = (agent.nativeName || '').toUpperCase();
      return code.includes(input) || name.includes(input) || native.includes(input);
  });

  const handlePreSubmitTransaction = () => {
    let finalAmount = 0;
    let detailMsg = '';

    if (activeTab === 'Chips Out' || activeTab === 'Settle') {
        const cVal = parseNum(cashChips);
        const rVal = parseNum(rollingChips);
        finalAmount = cVal + rVal;
        
        if (activeTab === 'Chips Out' && finalAmount <= 0) return alert("Please enter chips amount.");
        if (activeTab === 'Settle' && finalAmount === 0) {
            if(!window.confirm("Return chips is 0. Proceed to settle?")) return;
        }
        
        detailMsg = (
            <div style={{ textAlign: 'left', background: '#f8fafc', padding: '10px', borderRadius: '6px', fontSize: '14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                    <span style={{ color:'#64748b' }}>{activeTab === 'Settle' ? 'Return Cash Chips' : 'Cash Chips'}:</span>
                    <strong style={{ color:'#0f172a' }}>{fmt(cVal)}</strong>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                    <span style={{ color:'#64748b' }}>{activeTab === 'Settle' ? 'Return Rolling Chips' : 'Rolling Chips'}:</span>
                    <strong style={{ color:'#d97706' }}>{fmt(rVal)}</strong>
                </div>
                <div style={{ borderTop:'1px solid #e2e8f0', margin:'5px 0' }}></div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontWeight:'bold' }}>Total Return:</span>
                    <strong style={{ color:'#2563eb', fontSize:'16px' }}>{fmt(finalAmount)}</strong>
                </div>
            </div>
        );
    } else {
        finalAmount = parseNum(amount);
        if (finalAmount <= 0) return alert("Please enter amount.");
        if (activeTab === 'Add Buy-in') {
            const agent = safeAgents.find(a => a.code === selectedGame.agentId);
            const currentBal = agent ? parseFloat(agent.balance || 0) : 0;
            
            if (selectedGame.type !== 'Credit' && currentBal < finalAmount) {
                alert(`${t('msg_short')} Current: ${fmt(currentBal)} / Request: ${fmt(finalAmount)}`);
                return;
            }
        }
        detailMsg = <div style={{ fontSize:'18px', fontWeight:'bold', color:'#2563eb', textAlign:'center' }}>{fmt(finalAmount)}</div>;
    }

    setTxConfirm({
        isOpen: true,
        data: {
            type: activeTab === 'Chips Out' ? 'Chips Out' : activeTab,
            amount: finalAmount,
            cashChips: parseNum(cashChips),
            rollingChips: parseNum(rollingChips),
            remark: remark,
            detailMsg: detailMsg
        }
    });
  };

  const handleFinalSubmitTransaction = async () => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      const { type, amount, cashChips, rollingChips, remark } = txConfirm.data;
      const agent = safeAgents.find(a => a.code === selectedGame.agentId);
      
      try {
          let finalRemark = remark;
          if (type === 'Chips Out' || type === 'Settle') {
              const breakdown = `(Cash: ${fmt(cashChips)} / Rolling: ${fmt(rollingChips)})`;
              finalRemark = remark ? `${remark} ${breakdown}` : breakdown;
          }

          await addTransaction(selectedGame.id, { type, amount, cashChips, rollingChips, remark: finalRemark });

          if (type === 'Settle') {
              const currentTotalBuyIn = parseNum(selectedGame.sumBuyIn || 0);
              const currentSumCashOut = parseNum(selectedGame.sumCashOut || 0);
              const currentTotalRolling = parseNum(selectedGame.totalRolling || 0);

              const finalTotalCashOut = currentSumCashOut + amount;
              const returnRollingChips = parseNum(rollingChips);
              const finalTotalRolling = Math.max(0, currentTotalRolling - returnRollingChips);
              const finalWinAmount = currentTotalBuyIn - finalTotalCashOut;

              const rollingCommRate = parseFloat(selectedGame.rollingComm || 0);
              const rollingCommAmount = Math.floor(finalTotalRolling * (rollingCommRate / 100));

              const updatedGame = {
                  ...selectedGame,
                  status: 'Settled',
                  sumCashOut: finalTotalCashOut, 
                  totalCashOut: finalTotalCashOut, 
                  totalRolling: finalTotalRolling, 
                  winAmount: finalWinAmount,
                  endDate: new Date().toISOString(),
                  settleRemark: finalRemark
              };
              
              if (updateGame) await updateGame(updatedGame);

              const settlementData = {
                  date: new Date().toISOString(),
                  gameId: selectedGame.id,
                  agentCode: selectedGame.agentId,
                  agentName: agent ? `${agent.engSurname} ${agent.engGivenName}` : selectedGame.agentId,
                  type: selectedGame.type,
                  totalBuyIn: currentTotalBuyIn,
                  totalReturn: amount,
                  totalCashOut: finalTotalCashOut,
                  winAmount: finalWinAmount,
                  totalRolling: finalTotalRolling,
                  rollingComm: selectedGame.rollingComm,
                  rollingCommAmount: rollingCommAmount,
                  shareRate: selectedGame.shareRate, 
                  status: 'Completed',
                  remark: finalRemark || 'Game Settled',
                  settledBy: 'MASTER_ADMIN'
              };
              if (addSettlement) await addSettlement(settlementData);

              setIsModalOpen(false); 
              alert("Settlement Completed.");
          } 
          else if (type === 'Chips Out') {
              alert("Chips Out transaction processed.");
          } 
          else {
              alert("Transaction successful.");
          }

          setTxConfirm({ isOpen: false, data: null });
          setAmount(''); setCashChips(''); setRollingChips(''); setRemark('');

      } catch (error) {
          console.error(error);
          alert("Error processing transaction.");
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleActionClick = (game) => {
      setSelectedGameId(game.id); 
      handleTabChange('Rolling');
      setIsModalOpen(true); 
  };

  const handleCreateGameSubmit = async () => {
    if (isSubmitting) return;

    if (!newGameData.agentId) return alert("Agent ID is required.");
    if (!newGameData.buyIn) return alert("Buy-in amount is required.");

    const agent = safeAgents.find(a => a.code === newGameData.agentId);
    if (!agent) return alert("Agent not found.");

    const buyInVal = parseNum(newGameData.buyIn);
    if ((agent.balance || 0) < buyInVal && newGameData.type !== 'Credit') {
        return alert("Insufficient agent balance.");
    }
    
    if (newGameData.type === 'Share') {
        const agentShareCap = parseFloat(agent.shareCap || 0); 
        const inputShareRate = parseFloat(newGameData.shareRate || 0);
        
        if (agentShareCap > 0 && inputShareRate > agentShareCap) {
            return alert(`Share Cap Error: Max ${agentShareCap}%`);
        }
    }

    setIsSubmitting(true);

    try {
        const nextGameId = generateNextGameId();
        const remarkString = `Game Creation: ${newGameData.type} (Game ID: ${nextGameId})`;

        const gamePayload = {
            ...newGameData,
            id: nextGameId, 
            buyIn: buyInVal, 
            initialRemark: remarkString 
        };

        const createdId = await createGame(gamePayload);
        
        if (createdId) {
            setIsCreateModalOpen(false);
            setSuccessModal({ isOpen: true, gameId: createdId || nextGameId });
            setNewGameData({ agentId: '', currency: settings?.mainCurrency || 'PHP', type: 'Rolling', buyIn: '', rollingComm: '', shareRate: '' });
        }
    } catch (error) {
        console.error("Game Creation Error:", error);
        alert("Error creating game.");
    } finally {
        setIsSubmitting(false);
    }
  };

  const foundAgentForNewGame = safeAgents.find(a => a.code === newGameData.agentId);

  return (
    <div style={{ padding: '30px', color: '#1e293b' }}>
      {/* --- Header Section --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>🎮 {t('menu_games')}</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => window.print()} style={{ backgroundColor: '#dc2626', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer' }}><FileText size={18}/> {t('pdf')}</button>
          
          {/* ★ isWritable 권한 체크 */}
          {isWritable && (
            <button onClick={() => setIsCreateModalOpen(true)} style={{ backgroundColor: '#2563eb', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer' }}><Plus size={18}/> {t('game_new')}</button>
          )}
        </div>
      </div>

      {/* --- Filter Section --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', backgroundColor:'#f8fafc', padding:'15px', borderRadius:'8px', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['All', 'Rolling', 'Share', 'Credit'].map(type => (
            <button key={type} onClick={() => setFilterType(type)} style={{ padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 'bold', backgroundColor: filterType === type ? '#0f172a' : '#e2e8f0', color: filterType === type ? 'white' : '#64748b' }}>{type === 'All' ? t('filter_all') : type}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems:'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #ccc', borderRadius: '6px', padding: '5px 10px' }}>
                <Calendar size={16} color="#64748b" style={{ marginRight: '8px' }} />
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#334155', cursor:'pointer' }} />
                <span style={{ margin: '0 8px', color: '#64748b' }}>~</span>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#334155', cursor:'pointer' }} />
            </div>
            <button onClick={() => { setStartDate(''); setEndDate(''); setSearchTerm(''); setFilterType('All'); }} title="Reset" style={{ padding: '8px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><RotateCcw size={16} color="#475569" /></button>
            <div style={{ position: 'relative' }}>
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={t('search')} style={{ padding: '8px 8px 8px 35px', borderRadius: '6px', border: '1px solid #ccc', width: '250px' }} />
                <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            </div>
        </div>
      </div>

      {/* --- Main Table --- */}
      <div id="main-table" style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', textAlign: 'center' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', height:'50px' }}>
              <th>Game ID</th><th style={{ textAlign: 'left', paddingLeft: '20px' }}>{t('menu_agents')}</th><th>{t('type')}</th><th>{t('totalBuyIn')}</th><th>{t('totalCashOut')}</th><th>{t('totalRolling')}</th><th>{t('status')}</th><th>{t('action')}</th>
            </tr>
          </thead>
          <tbody>
            {/* ★ [수정] currentGames (페이지네이션 적용) */}
            {currentGames.length > 0 ? currentGames.map(game => {
                const agent = safeAgents.find(a => a.code === game.agentId);
                return (
                    <tr key={game.id} style={{ borderBottom: '1px solid #f1f5f9', height:'60px' }}>
                        <td style={{ fontWeight: 'bold' }}>{game.id}</td>
                        <td style={{ textAlign: 'left', padding: '10px 20px' }}>
                            {agent ? (
                                <div onClick={(e) => handleAgentClick(e, agent.code)} style={{ cursor: 'pointer' }}>
                                    <span style={{ fontWeight: 'bold', color: '#2563eb', textDecoration: 'underline' }}>{agent.code}</span>
                                    <span style={{ margin: '0 5px', color: '#cbd5e1' }}>/</span>
                                    <span style={{ fontSize: '13px', color: '#1e293b' }}>{agent.engSurname} {agent.engGivenName}{agent.nativeName && <span style={{ color: '#64748b', marginLeft: '4px' }}>({agent.nativeName})</span>}</span>
                                </div>
                            ) : (
                                <div onClick={(e) => handleAgentClick(e, game.agentId)} style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 'bold', textDecoration: 'underline' }}>
                                    {game.agentId}
                                </div>
                            )}
                        </td>
                        <td><span style={{ padding:'4px 10px', borderRadius:'15px', fontSize:'12px', fontWeight:'bold', background: game.type==='Rolling'?'#eff6ff': game.type==='Share'?'#fff7ed':'#f0fdf4', color: game.type==='Rolling'?'#1d4ed8': game.type==='Share'?'#c2410c':'#15803d' }}>{game.type}</span></td>
                        <td style={{ fontWeight: 'bold' }}>{fmt(game.sumBuyIn)}</td>
                        <td style={{ color:'#dc2626' }}>{fmt(game.sumCashOut)}</td>
                        <td style={{ color:'#d97706', fontWeight:'bold' }}>{fmt(game.totalRolling)}</td>
                        <td><span style={{ fontWeight: 'bold', color: game.status==='Active'?'#16a34a':'#64748b' }}>{game.status === 'Active' ? t('active') : t('settled')}</span></td>
                        <td><button onClick={() => handleActionClick(game)} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: 'white', backgroundColor: game.status === 'Settled' ? '#64748b' : '#2563eb' }}>{game.status === 'Settled' ? t('game_view') : t('game_enter')}</button></td>
                    </tr>
                )
            }) : (<tr><td colSpan="8" style={{ padding:'20px', color:'#94a3b8' }}>No data</td></tr>)}
          </tbody>
        </table>
      </div>

      {/* ★ [추가] 페이지네이션 컨트롤 */}
      {totalPages > 1 && (
        <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
            <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
                <ChevronLeft size={20} />
            </button>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>
                Page {currentPage} of {totalPages}
            </span>
            <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
                <ChevronRight size={20} />
            </button>
        </div>
      )}
      
      {/* ... Other Modals ... */}
      {isCreateModalOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
              <div style={{ backgroundColor: 'white', width: '500px', borderRadius: '12px', padding: '30px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '20px' }}>🎲 {t('game_new')}</h3>
                  <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Agent ID</label><div style={{ position: 'relative' }}><input value={newGameData.agentId} onChange={handleAgentInputChange} placeholder="Search Agent Code or Name" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }} />{showAgentSuggestions && agentSuggestions.length > 0 && (<ul style={{ position: 'absolute', top: '100%', left: 0, width: '100%', maxHeight: '150px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '0 0 6px 6px', listStyle: 'none', padding: 0, margin: 0, zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>{agentSuggestions.map(agent => (<li key={agent.id} onClick={() => selectAgent(agent.code)} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}><strong>{agent.code}</strong> - {agent.engSurname} {agent.engGivenName}</li>))}</ul>)}</div>{foundAgentForNewGame && (<div style={{ marginTop: '5px', fontSize: '13px', color: '#2563eb', fontWeight: 'bold', textAlign: 'right' }}>{t('agent_balance')}: {fmt(foundAgentForNewGame.balance)}</div>)}</div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}><div style={{ flex: 1 }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>Currency</label><select value={newGameData.currency} onChange={e => setNewGameData({...newGameData, currency: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }}><option>KRW</option><option>PHP</option><option>USD</option><option>HKD</option></select></div><div style={{ flex: 1 }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>{t('type')}</label><select value={newGameData.type} onChange={e => setNewGameData({...newGameData, type: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }}><option value="Rolling">Rolling</option><option value="Share">Share</option><option value="Credit">Credit</option></select></div></div>
                  <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>{t('game_buyin')} {t('amount')}</label><input value={newGameData.buyIn} onChange={handleAmountChange((val)=>setNewGameData({...newGameData, buyIn: val}))} placeholder="0" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc', fontSize:'16px', fontWeight: 'bold' }} />{newGameData.type === 'Credit' && <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '5px' }}>* {t('msg_credit_warn')}</div>}</div>
                  {newGameData.type === 'Share' ? (<><div style={{ marginBottom: '15px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>Rolling Commission (%)</label><input value={newGameData.rollingComm} onChange={e => setNewGameData({...newGameData, rollingComm: e.target.value})} placeholder="Example: 1.2" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }} /></div><div style={{ marginBottom: '20px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>Share Rate (%)</label><input value={newGameData.shareRate} onChange={e => setNewGameData({...newGameData, shareRate: e.target.value})} placeholder="Example: 50" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }} /></div></>) : (<div style={{ marginBottom: '20px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>Rolling Commission (%)</label><input value={newGameData.rollingComm} onChange={e => setNewGameData({...newGameData, rollingComm: e.target.value})} placeholder="0.0" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc' }} /></div>)}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}><button onClick={() => setIsCreateModalOpen(false)} style={{ padding: '10px 20px', background: 'white', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' }}>{t('cancel')}</button><button onClick={handleCreateGameSubmit} disabled={isSubmitting} style={{ padding: '10px 20px', background: isSubmitting?'#9ca3af':'#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: isSubmitting?'not-allowed':'pointer', fontWeight: 'bold' }}>{isSubmitting?'Creating...':t('confirm')}</button></div>
              </div>
          </div>
      )}

      {successModal.isOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
              <div style={{ backgroundColor: 'white', width: '600px', padding: '50px', borderRadius: '25px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                  <div style={{ display:'flex', justifyContent:'center', marginBottom:'30px' }}><CheckCircle size={80} color="#16a34a" /></div>
                  <h2 style={{ fontSize: '32px', margin: '0 0 15px 0', color: '#1e293b' }}>Success!</h2>
                  <p style={{ color: '#64748b', fontSize: '18px' }}>New Game Created.</p>
                  <div style={{ margin: '40px 0', padding: '30px', backgroundColor: '#f0fdf4', borderRadius: '15px', border: '2px solid #bbf7d0' }}><div style={{ fontSize: '16px', color: '#166534', marginBottom: '10px', fontWeight:'bold' }}>GAME ID</div><div style={{ fontSize: '56px', fontWeight: '900', color: '#15803d', letterSpacing: '4px' }}>{successModal.gameId}</div></div>
                  <button onClick={() => setSuccessModal({ isOpen: false, gameId: '' })} style={{ width: '100%', padding: '20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '20px', fontWeight: 'bold', cursor: 'pointer' }}>{t('confirm')}</button>
              </div>
          </div>
      )}

      {/* Game Detail Modal */}
      {isModalOpen && selectedGame && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div id="game-detail-content" style={{ backgroundColor: 'white', width: '1100px', height: '85vh', borderRadius: '12px', padding: '30px', display: 'flex', flexDirection: 'column', position:'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '15px' }}>
                    <div>
                        <h2 style={{ margin: 0, color: '#0f172a' }}>{selectedGame.id} - {t('tab_game')}</h2>
                        <span style={{ color: '#64748b', fontSize: '14px' }}>Agent: {selectedGame.agentId} | Type: {selectedGame.type}</span>
                    </div>
                    <div style={{ display:'flex', gap:'10px' }}>
                        <button onClick={() => window.print()} className="no-print" style={{ backgroundColor: '#dc2626', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '6px', cursor:'pointer', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'5px' }}><FileText size={16}/> {t('pdf')}</button>
                        <button onClick={() => setIsModalOpen(false)} className="no-print" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={28} /></button>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '20px', flex: 1, overflow: 'hidden' }}>
                   {/* Left Panel */}
                   <div className="no-print" style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '15px', borderRight: '1px solid #e2e8f0', paddingRight: '20px' }}>
                       {selectedGame.status === 'Settled' ? (
                           <div style={{ textAlign: 'center', padding: '30px 20px', background: '#f1f5f9', borderRadius: '8px', border: '1px dashed #cbd5e1', marginTop: '20px' }}>
                               <CheckCircle size={48} color="#16a34a" style={{ marginBottom: '15px', display: 'block', margin: '0 auto 15px auto' }} />
                               <h3 style={{ margin: '0 0 10px 0', color: '#0f172a' }}>{t('settled')}</h3>
                               <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>Game is closed.</p>
                               <button onClick={() => setIsSettlementModalOpen(true)} style={{ width: '100%', padding: '15px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)' }}><Receipt size={20}/> {t('menu_reports')}</button>
                           </div>
                       ) : (
                           <>
                               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                   {['Rolling', 'Settle', 'Add Buy-in', 'Chips Out'].filter(tab => { if (selectedGame.type === 'Credit' && tab === 'Chips Out') return false; return true; }).map(tab => (
                                     <button key={tab} onClick={() => handleTabChange(tab)} style={{ padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 'bold', backgroundColor: activeTab === tab ? (tab==='Rolling'?'#22c55e': tab==='Settle'?'#64748b': tab==='Add Buy-in'?'#3b82f6':'#ef4444') : '#f1f5f9', color: activeTab === tab ? 'white' : '#64748b' }}>
                                         {tab === 'Rolling' ? t('game_rolling') : tab === 'Settle' ? t('game_settle') : tab === 'Add Buy-in' ? t('game_add_buyin') : t('game_cashout')}
                                     </button>
                                   ))}
                               </div>
                               <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', flex: 1 }}>
                                   <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#334155' }}>{activeTab}</h3>
                                   {(activeTab === 'Chips Out' || activeTab === 'Settle') ? (
                                        <>
                                            <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>{activeTab === 'Settle' ? 'Return Cash Chips' : 'Cash Chips'}</label><input value={cashChips} onChange={handleAmountChange(setCashChips)} placeholder="0" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '16px' }} /></div>
                                            <div style={{ marginBottom: '15px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#d97706' }}>{activeTab === 'Settle' ? 'Return Rolling Chips' : 'Rolling Chips'}</label><input value={rollingChips} onChange={handleAmountChange(setRollingChips)} placeholder="0" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '16px' }} /></div>
                                            <div style={{ marginBottom: '20px', background: '#e2e8f0', padding: '10px', borderRadius: '6px', textAlign: 'right' }}><span style={{ fontSize: '12px', color: '#64748b' }}>{activeTab === 'Settle' ? 'Total Return Chips' : 'Total Chips Out'}</span><div style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a' }}>{fmt(parseNum(cashChips) + parseNum(rollingChips))}</div></div>
                                        </>
                                    ) : (
                                        <div style={{ marginBottom: '15px' }}>
                                            {activeTab === 'Add Buy-in' && selectedGame && safeAgents.find(a => a.code === selectedGame.agentId) && (<div style={{ marginBottom: '8px', textAlign: 'right', fontSize: '13px', color: '#2563eb', fontWeight: 'bold' }}>{t('agent_balance')}: {fmt(safeAgents.find(a => a.code === selectedGame.agentId).balance)}</div>)}
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>{t('amount')}</label>
                                            <input value={amount} onChange={handleAmountChange(setAmount)} placeholder="0" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '16px', fontWeight: 'bold' }} />
                                        </div>
                                    )}
                                    <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>{t('remark')}</label><input value={remark} onChange={e => setRemark(e.target.value)} placeholder="..." style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }} /></div>
                                    <button onClick={handlePreSubmitTransaction} style={{ width: '100%', padding: '15px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>{t('game_enter')}</button>
                                </div>
                           </>
                       )}
                   </div>

                    {/* Right Panel (Transaction History) */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginBottom: '15px' }}>
                            <div style={{ background: '#f1f5f9', padding: '10px 20px', borderRadius: '8px' }}><span style={{ display: 'block', fontSize: '12px', color: '#64748b' }}>{t('totalBuyIn')}</span><strong style={{ fontSize: '18px', color: '#0f172a' }}>{fmt(selectedGame.sumBuyIn)}</strong></div>
                            <div style={{ background: '#fff7ed', padding: '10px 20px', borderRadius: '8px' }}><span style={{ display: 'block', fontSize: '12px', color: '#c2410c' }}>{t('totalRolling')}</span><strong style={{ fontSize: '18px', color: '#ea580c' }}>{fmt(selectedGame.totalRolling)}</strong></div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'center' }}>
                                <thead style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}><th>{t('date')}</th><th>{t('type')}</th><th style={{ textAlign: 'right' }}>{t('amount')}</th><th>{t('remark')}</th><th>{t('staff')}</th></tr>
                                </thead>
                                <tbody>
                                    {(transactions && transactions[selectedGame.id]) ? transactions[selectedGame.id].map((tx, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px' }}>
                                                {new Date(tx.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}&nbsp; 
                                                {new Date(tx.date).toLocaleTimeString()}
                                            </td>
                                            <td style={{ padding: '12px' }}><span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', color: tx.type === 'Settle' ? '#dc2626' : tx.type === 'Chips Out' ? '#b91c1c' : '#0f172a', background: tx.type === 'Settle' ? '#fef2f2' : tx.type === 'Chips Out' ? '#fef2f2' : '#f1f5f9' }}>{tx.type}</span></td>
                                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold' }}>{fmt(tx.amount)}</td>
                                            <td style={{ padding: '12px', color: '#64748b' }}>{tx.remark}</td>
                                            <td style={{ padding: '12px' }}>{tx.staff}</td>
                                        </tr>
                                    )) : <tr><td colSpan="5">No Transactions</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm Modal */}
            {txConfirm.isOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 }}>
                    <div style={{ backgroundColor: 'white', width: '350px', borderRadius: '12px', padding: '25px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                            <AlertCircle size={24} color="#2563eb" />
                            <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>{t('confirm')}</h3>
                        </div>
                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ margin: '0 0 10px 0', color: '#64748b', fontSize: '14px' }}>Proceed with this transaction?</p>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Type: <strong style={{ color: '#0f172a' }}>{txConfirm.data.type}</strong></div>
                                {txConfirm.data.detailMsg}
                                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '10px' }}>Remark: {txConfirm.data.remark || '-'}</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={handleFinalSubmitTransaction} disabled={isSubmitting} style={{ flex: 1, padding: '12px', backgroundColor: isSubmitting?'#9ca3af':'#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: isSubmitting?'not-allowed':'pointer' }}>{isSubmitting?'Processing...':t('confirm')}</button>
                            <button onClick={() => setTxConfirm({ isOpen: false, data: null })} style={{ flex: 1, padding: '12px', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>{t('cancel')}</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @media print {
                    @page { size: auto; margin: 0mm; }
                    html, body { height: 1px !important; overflow: hidden !important; margin: 0 !important; padding: 0 !important; background: white !important; }
                    body * { visibility: hidden; }
                    #print-overlay, #print-overlay * { visibility: visible; }
                    #print-overlay { position: absolute !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; margin: 0 !important; padding: 20px !important; background: white !important; z-index: 99999 !important; }
                    .no-print { display: none !important; }
                }
            `}</style>
        </div>
      )}

      {/* 5. Settlement Sheet Modal */}
      {isSettlementModalOpen && selectedGame && (
          <SettlementDetailModal 
            game={selectedGame} 
            agent={agents.find(a => a.code === selectedGame.agentId)}
            onClose={() => setIsSettlementModalOpen(false)} 
          />
      )}
    </div>
  );
};

export default GameList;